import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DataUsage,
  ModelTraining,
  Assessment,
  Speed,
  Security,
  CloudUpload,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <DataUsage sx={{ fontSize: 48 }} />,
      title: 'Data Management',
      description: 'Upload and manage spectroscopy data from multiple sensors with ease.',
    },
    {
      icon: <ModelTraining sx={{ fontSize: 48 }} />,
      title: 'AI-Powered Models',
      description: 'Train machine learning/Deep learning models for classification and prediction tasks.',
    },
    {
      icon: <Assessment sx={{ fontSize: 48 }} />,
      title: 'Advanced Analytics',
      description: 'Visualize data with PCA, UMAP, and Mahalanobis distance plots.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'Fast Processing',
      description: 'Process large datasets quickly with optimized algorithms.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'Secure & Reliable',
      description: 'Your data is protected with industry-standard security measures.',
    },
    {
      icon: <CloudUpload sx={{ fontSize: 48 }} />,
      title: 'Easy Import',
      description: 'Import CSV files with a simple drag-and-drop interface.',
    },
  ];

  return (
    <MainLayout>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 4,
          color: 'white',
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '50%',
            height: '100%',
            borderRadius: '50%',
            background: alpha('#ffffff', 0.1),
            filter: 'blur(80px)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                Digital Spectra
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              >
                Advanced Spectroscopy Data Management & AI Analysis Platform
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem' }}>
                Transform your spectroscopy workflows with powerful data management,
                machine learning models, and comprehensive visualization tools.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {isAuthenticated ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/projects')}
                    endIcon={<ArrowForward />}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        bgcolor: alpha('#ffffff', 0.9),
                      },
                    }}
                  >
                    Go to Projects
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/login')}
                    endIcon={<ArrowForward />}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        bgcolor: alpha('#ffffff', 0.9),
                      },
                    }}
                  >
                    Get Started
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 300,
                    height: 300,
                    borderRadius: '50%',
                    background: alpha('#ffffff', 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(20px)',
                  }}
                >
                  <DataUsage sx={{ fontSize: 150, opacity: 0.9 }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Powerful Features
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Everything you need for spectroscopy data analysis
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                      color: theme.palette.primary.main,
                      mb: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </MainLayout>
  );
};

export default Home;
